import React, { useMemo } from "react";
import { Box, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Tooltip } from "@material-ui/core";
import { Lock, LockOpen } from "@material-ui/icons";
import { Controller, useFormContext } from "react-hook-form";
import { TemplateFormData, TemplateFormExercise } from "../types";
import { MAX_SETS } from "../constants";

type Props = {
  index: number;
  maxWeeks: number;
  readOnly?: boolean;
  // Field snapshot for this row. RHF v6 does not wire useFieldArray values into
  // nested Controllers on append, so each Controller is seeded via defaultValue
  // from here (otherwise appended exercises render blank inputs).
  defaults: TemplateFormExercise;
};

const range = (max: number): number[] => Array.from({ length: Math.max(1, max) }, (_, i) => i + 1);
const SETS_OPTIONS = range(MAX_SETS);

// Inline row of per-exercise controls: start/end week, level (+lock), sets, reps.
const ExerciseRow: React.VFC<Props> = ({ index, maxWeeks, defaults, readOnly = false }) => {
  const { control } = useFormContext<TemplateFormData>();
  const base = `exercises.${index}` as const;

  const maxLevel = defaults.max_level ?? 1;
  const weeks = useMemo(() => range(maxWeeks), [maxWeeks]);
  const levels = useMemo(() => range(maxLevel), [maxLevel]);

  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
      <Controller
        control={control}
        name={`${base}.start_week` as const}
        defaultValue={defaults.start_week}
        render={({ onChange, value }) => (
          <FormControl variant="outlined" size="small" style={{ minWidth: 120 }} disabled={readOnly}>
            <InputLabel>Start week</InputLabel>
            <Select label="Start week" value={value} onChange={e => onChange(Number(e.target.value))}>
              {weeks.map(w => (
                <MenuItem key={w} value={w}>
                  Week {w}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <Controller
        control={control}
        name={`${base}.end_week` as const}
        defaultValue={defaults.end_week}
        render={({ onChange, value }) => (
          <FormControl variant="outlined" size="small" style={{ minWidth: 120 }} disabled={readOnly}>
            <InputLabel>End week</InputLabel>
            <Select
              label="End week"
              value={value == null ? "ongoing" : value}
              onChange={e => onChange(e.target.value === "ongoing" ? null : Number(e.target.value))}
            >
              <MenuItem value="ongoing">Ongoing</MenuItem>
              {weeks.map(w => (
                <MenuItem key={w} value={w}>
                  Week {w}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <Controller
        control={control}
        name={`${base}.recommended_level` as const}
        defaultValue={defaults.recommended_level}
        render={({ onChange, value }) => (
          <FormControl variant="outlined" size="small" style={{ minWidth: 90 }} disabled={readOnly}>
            <InputLabel>Level</InputLabel>
            <Select label="Level" value={value} onChange={e => onChange(Number(e.target.value))}>
              {levels.map(l => (
                <MenuItem key={l} value={l}>
                  {l}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <Controller
        control={control}
        name={`${base}.level_locked` as const}
        defaultValue={defaults.level_locked}
        render={({ onChange, value }) => (
          <Tooltip title={value ? "Level locked" : "Level unlocked"}>
            <span>
              <IconButton
                size="small"
                disabled={readOnly}
                aria-label={value ? "Unlock level" : "Lock level"}
                onClick={() => onChange(!value)}
              >
                {value ? <Lock fontSize="small" /> : <LockOpen fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      />

      <Controller
        control={control}
        name={`${base}.sets` as const}
        defaultValue={defaults.sets}
        render={({ onChange, value }) => (
          <FormControl variant="outlined" size="small" style={{ minWidth: 80 }} disabled={readOnly}>
            <InputLabel>Sets</InputLabel>
            <Select label="Sets" value={value} onChange={e => onChange(Number(e.target.value))}>
              {SETS_OPTIONS.map(s => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <Controller
        control={control}
        name={`${base}.reps` as const}
        defaultValue={defaults.reps}
        render={({ onChange, value }) => (
          <TextField
            label="Repetitions"
            variant="outlined"
            size="small"
            style={{ width: 110 }}
            disabled={readOnly}
            value={value}
            onChange={e => onChange(e.target.value)}
          />
        )}
      />
    </Box>
  );
};

export default ExerciseRow;
